import { Graphics } from 'pixi.js';
import { BOSS_MECHANICS } from '@/config/boss-mechanics';

const C = BOSS_MECHANICS.webspinner;
const TAU = Math.PI * 2;
const SPAN = TAU / C.panels;

/** Silk look — cream like the web strands, lilac shade like the carapace. */
const LOOK = {
  inner: 34,
  outer: 66,
  /** Wrapping threads per panel (inner → outer) */
  wraps: 3,
  /** Angular gap between panels (rad) */
  gap: 0.06,
  silk: 0xeee2cc,
  sheen: 0xfff8ea,
  shade: 0x9b8fb5,
  /** Sheet fill alpha at full silk; threads fade from max → min as it thins */
  sheet: 0.16,
  threadMax: 0.85,
  threadMin: 0.25,
  flash: 0.08,
  /** Dangling stubs on a torn panel (px) + their sway (rad/s) */
  stub: 16,
  sway: 2.4,
  shuttle: 3.2,
} as const;

interface Panel {
  hp: number;
  /** Gaping time left once torn (s); 0 = not torn */
  open: number;
  knitting: boolean;
  flash: number;
}

/** Signed wrap of an angle to (−π, π]. */
function wrap(a: number): number {
  let d = a % TAU;
  if (d > Math.PI) d -= TAU;
  if (d <= -Math.PI) d += TAU;
  return d;
}

/**
 * Webspinner's silk cocoon (docs/02 wave 4): panels of silk around the
 * body. Pure state + one Graphics; the boss routes every hit through
 * `filter()` and gets back the share that reaches the body.
 */
export class Cocoon {
  readonly view = new Graphics();
  private readonly panels: Panel[] = [];
  private full = 1;
  private time = 0;

  constructor(private readonly onTear: (angle: number) => void) {
    for (let i = 0; i < C.panels; i++) {
      this.panels.push({ hp: 0, open: 0, knitting: false, flash: 0 });
    }
  }

  /** (Re)spin every panel to full silk for a boss of `bossMaxHp`. */
  weave(bossMaxHp: number): void {
    this.full = bossMaxHp * C.panelHp;
    for (const p of this.panels) {
      p.hp = this.full;
      p.open = 0;
      p.knitting = false;
    }
  }

  /** Panel facing a world angle around the body. */
  private at(angle: number): Panel {
    const k = Math.round(wrap(angle) / SPAN);
    return this.panels[(k + C.panels) % C.panels]!;
  }

  /** True when intact silk faces this angle (the hit will be damped). */
  covers(angle: number): boolean {
    return this.at(angle).hp > 0;
  }

  /**
   * A hit arriving from `angle`: intact silk catches it and loses `dmg`
   * silk; a gaping panel lets it through. @returns damage that reaches the body
   */
  filter(angle: number, dmg: number): number {
    const p = this.at(angle);
    if (p.hp <= 0) return dmg;
    p.hp -= dmg;
    p.flash = LOOK.flash;
    if (p.hp <= 0) {
      p.hp = 0;
      p.open = C.openTime;
      p.knitting = false;
      this.onTear(Math.round(wrap(angle) / SPAN) * SPAN);
    }
    return dmg * C.through;
  }

  update(dt: number): void {
    this.time += dt;
    for (const p of this.panels) {
      p.flash -= dt;
      if (p.open > 0) {
        p.open -= dt;
        if (p.open <= 0) p.knitting = true;
      } else if (p.knitting) {
        p.hp += (this.full / C.knitTime) * dt;
        if (p.hp >= this.full) {
          p.hp = this.full;
          p.knitting = false;
        }
      }
    }
  }

  /** Silk sheets + wrapping threads; torn panels sway loose stubs. */
  render(): void {
    const g = this.view;
    g.clear();
    for (let k = 0; k < C.panels; k++) {
      const p = this.panels[k]!;
      const a0 = k * SPAN - SPAN / 2 + LOOK.gap;
      const a1 = k * SPAN + SPAN / 2 - LOOK.gap;
      if (p.hp <= 0) {
        const sway = Math.sin(this.time * LOOK.sway + k) * 0.25;
        this.stub(g, a0, sway);
        this.stub(g, a1, sway);
        continue;
      }
      const frac = p.hp / this.full;
      const lit = p.flash > 0;
      g.moveTo(Math.cos(a0) * LOOK.outer, Math.sin(a0) * LOOK.outer)
        .arc(0, 0, LOOK.outer, a0, a1).arc(0, 0, LOOK.inner, a1, a0, true).closePath()
        .fill({ color: LOOK.silk, alpha: LOOK.sheet * frac });
      const alpha = LOOK.threadMin + (LOOK.threadMax - LOOK.threadMin) * frac;
      for (let j = 0; j < LOOK.wraps; j++) {
        const r = LOOK.inner + ((LOOK.outer - LOOK.inner) * (j + 0.5)) / LOOK.wraps;
        g.moveTo(Math.cos(a0) * r, Math.sin(a0) * r).arc(0, 0, r, a0, a1).stroke({
          color: lit ? LOOK.sheen : LOOK.silk, width: lit ? 3.5 : 2.5, alpha,
        });
      }
      // Cross-threads zig-zag over the wraps (the silk's weave)
      g.moveTo(Math.cos(a0) * LOOK.inner, Math.sin(a0) * LOOK.inner)
        .lineTo(Math.cos(a1) * LOOK.outer, Math.sin(a1) * LOOK.outer)
        .moveTo(Math.cos(a0) * LOOK.outer, Math.sin(a0) * LOOK.outer)
        .lineTo(Math.cos(a1) * LOOK.inner, Math.sin(a1) * LOOK.inner)
        .stroke({ color: LOOK.shade, width: 1.5, alpha: alpha * 0.8 });
      if (p.knitting) {
        // The spinneret shuttle runs across the hole while it knits
        const s = a0 + (a1 - a0) * (0.5 + 0.5 * Math.sin(this.time * LOOK.shuttle * TAU));
        g.circle(Math.cos(s) * LOOK.outer, Math.sin(s) * LOOK.outer, 3)
          .fill({ color: LOOK.sheen, alpha: 0.9 });
      }
    }
  }

  /** A shot-apart panel edge: a loose silk stub swaying in the gap. */
  private stub(g: Graphics, a: number, sway: number): void {
    const bx = Math.cos(a) * LOOK.outer;
    const by = Math.sin(a) * LOOK.outer;
    const out = a + sway;
    g.moveTo(bx, by)
      .lineTo(bx + Math.cos(out) * LOOK.stub, by + Math.sin(out) * LOOK.stub)
      .stroke({ color: LOOK.silk, width: 2, alpha: 0.55 });
  }
}
